import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import HeroCarousel from '../components/HeroCarousel';
import HeroPlaceholder from '../components/HeroPlaceholder';
import TrustPillars from '../components/TrustPillars';
import MattingCalculator from '../components/MattingCalculator';
import ProductHubsGrid from '../components/ProductHubsGrid';
import FieldConditions from '../components/FieldConditions';
import WhyCenturion from '../components/WhyCenturion';
import CtaBand from '../components/CtaBand';
import SEO from '../components/SEO';
import { HERO_MEDIA } from '../config/media';
import { apiClient } from '../lib/api';
import { orgJsonLd, websiteJsonLd, breadcrumbJsonLd } from '../lib/seo';

// Static fallback slide - MUST mirror centurion_hero_slides sort_order=0.
// Rendered as the SSR skeleton before /api/hero-slides resolves. Any drift
// from the live sort_order=0 slide will visibly flicker on first paint.
const FALLBACK_SLIDE = {
    id: 'fallback-1',
    sort_order: 0,
    image_url: HERO_MEDIA.truckLoaded.src,
    eyebrow: 'Manufacturer Direct • Since 1955',
    headline_main: 'Manufacturer Direct.',
    headline_accent: 'Built for the Most Difficult Terrain.',
    subtitle: 'High-quality matting products for oil & gas, power & transmission, and infrastructure - engineered for Western Canada\u2019s most difficult terrain.',
    cta_primary_label: 'Get a Manufacturer Direct Quote',
    cta_primary_href: '/quote',
    cta_secondary_label: 'Run the Numbers \u2193',
    cta_secondary_href: '#calculator',
};

export default function Home() {
    const [slides, setSlides] = useState(null);
    const location = useLocation();

    // Smooth-scroll to the calculator when arriving with #calculator or
    // #field-calculator hash (e.g. via the header Products mega-menu).
    useEffect(() => {
        if (!location.hash) return;
        const id = location.hash.replace('#', '');
        // Wait one frame for the section to mount before scrolling.
        const t = setTimeout(() => {
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 60);
        return () => clearTimeout(t);
    }, [location.hash, location.key]);

    useEffect(() => {
        let cancelled = false;
        apiClient
            .get('/hero-slides')
            .then((res) => {
                if (cancelled) return;
                const list = Array.isArray(res.data) && res.data.length ? res.data : [FALLBACK_SLIDE];
                setSlides(list);
            })
            .catch(() => {
                if (!cancelled) setSlides([FALLBACK_SLIDE]);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <>
            <SEO
                title="Manufacturer Direct Timber Mats - Access, Rig & Crane"
                description="Centurion Matting - Manufacturer and wholesaler of high-quality matting for oil & gas, power, transmission, and infrastructure. Family-owned since 1955."
                cmsSlug="seo.home"
                path="/"
                image={HERO_MEDIA.truckLoaded.src}
                jsonLd={[orgJsonLd(), websiteJsonLd(), breadcrumbJsonLd([{ name: 'Home', path: '/' }])]}
            />

            {slides === null ? (
                /* SSR/skeleton: render the fallback slide statically before fetch completes */
                <HeroPlaceholder
                    mediaKey="truckLoaded"
                    eyebrow={FALLBACK_SLIDE.eyebrow}
                    title={
                        <>
                            {FALLBACK_SLIDE.headline_main}{' '}
                            <span className="text-brand-gold">{FALLBACK_SLIDE.headline_accent}</span>
                        </>
                    }
                    subtitle={FALLBACK_SLIDE.subtitle}
                    height="h-[520px] md:h-[640px]"
                />
            ) : (
                <HeroCarousel slides={slides} />
            )}

            <TrustPillars />

            <section
                id="field-calculator"
                className="mx-auto max-w-7xl scroll-mt-24 px-6 py-20 md:px-10 md:py-28"
            >
                {/* Anchor alias used by the header Products → Field Calculator link */}
                <span id="calculator" aria-hidden="true" className="block -mt-24 pt-24" />
                <span className="eyebrow">The Field Calculator</span>
                <h2 className="h-display mt-6 max-w-3xl text-3xl sm:text-4xl lg:text-5xl">
                    Spec your job in 30 seconds.
                </h2>
                <p className="mt-5 max-w-2xl font-sans text-base text-brand-muted">
                    Pick your mode, type your numbers, hand the result to our sales team.
                    The math is the same one our yard runs.
                </p>
                <div className="mt-10">
                    <MattingCalculator variant="full" />
                </div>
            </section>

            <ProductHubsGrid />
            <FieldConditions />
            <WhyCenturion />

            <CtaBand
                eyebrow="Manufacturer Direct"
                title="Generate Your Field Spec Report"
                body="Hand your numbers to a Centurion sales lead. Manufacturer Direct pricing, lead times, and delivery from BC."
                cta={{ to: '/quote', label: 'Get a Quote' }}
                secondary={{ to: '/contact', label: 'Talk to Sales' }}
            />
        </>
    );
}
